import { customerQuery, useArchiveCustomerMutation } from "@common/api"
import { workPackagesByCustomerQuery } from "@common/api/work-packages"
import { ConfirmDialog } from "@common/components/shared/confirm-dialog"
import { handleError } from "@common/utils"
import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import useCustomToast from "@/hooks/use-custom-toast"

export const Route = createFileRoute("/_layout/customer_/$customerId/delete")({
  component: DeleteCustomerModal,
})

function DeleteCustomerModal(): React.ReactElement {
  const { customerId } = Route.useParams()
  const navigate = useNavigate()
  const showToast = useCustomToast()

  const { data: customer } = useSuspenseQuery(
    customerQuery({ customer_id: customerId }),
  )

  const { data: workPackages } = useQuery(
    workPackagesByCustomerQuery({ customer_id: customerId }),
  )

  const archiveMutation = useArchiveCustomerMutation()

  const close = () =>
    navigate({ to: "/customer/$customerId", params: { customerId } })

  const confirm = () =>
    archiveMutation.mutate(
      { customer_id: customerId },
      {
        onSuccess: () => {
          showToast("Success", "Customer archived", "success")
          navigate({ to: "/customer" })
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  return (
    <ConfirmDialog
      title="Archive customer?"
      description={
        <>
          This removes the customer from the directory and from customer
          pickers. Existing work orders and quotes keep their link to them. You
          can restore the customer from the Archived tab.
          <span className="mt-2 block break-words text-body-medium text-foreground">
            {customer.name || "Unnamed customer"}
          </span>
          {workPackages && (
            <span className="mt-2 block">
              {workOrderSummary(workPackages.count)}
            </span>
          )}
        </>
      }
      action="Archive"
      tone="default"
      pendingAction="Archiving…"
      pending={archiveMutation.isPending}
      onConfirm={confirm}
      onCancel={close}
    />
  )
}

function workOrderSummary(count: number): string {
  switch (count) {
    case 0:
      return "No work orders reference this customer."
    case 1:
      return "1 work order references this customer."
    default:
      return `${count} work orders reference this customer.`
  }
}
